/**
 * React Query hooks for Rejection Reason operations.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import rejectionReasonService from '../services/rejection-reason.service';

interface UseRejectionReasonListQueryParams {
  workspaceId?: string;
  idToken: string | undefined;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useRejectionReasonListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 50,
  sortBy = 'reason',
  order = 'asc',
  search,
  enabled = true,
  refetchInterval = 5000,
}: UseRejectionReasonListQueryParams) => {
  return useQuery({
    queryKey: ['rejection-reasons', workspaceId, page, size, sortBy, order, search],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }

      return await rejectionReasonService.listRejectionReasons(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        search,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval,
    refetchIntervalInBackground: true,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseRejectionReasonDetailQueryParams {
  rejectionReasonId: string | undefined;
  workspaceId?: string;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useRejectionReasonDetailQuery = ({
  rejectionReasonId,
  workspaceId,
  idToken,
  enabled = true,
}: UseRejectionReasonDetailQueryParams) => {
  return useQuery({
    queryKey: ['rejection-reason', workspaceId, rejectionReasonId],
    queryFn: async () => {
      if (!rejectionReasonId || !workspaceId || !idToken) {
        throw new Error('Rejection reason ID, workspace ID, and token are required');
      }

      return await rejectionReasonService.getRejectionReason(workspaceId, rejectionReasonId, idToken);
    },
    enabled: enabled && !!workspaceId && !!rejectionReasonId && !!idToken,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};
